import { useMemo } from 'react';
import type { EventTimeline } from '../../core/Timelines';
import type { PnlData } from '../../hooks/usePnlData';
import { compact, memoize } from 'lodash';
import { getTimelineLoadedAmounts, type LoadedAmount } from '../loadedAmounts';
import {
  VoyageCharterCostType,
  type VoyageCosts_VoyageCharterFragment,
} from '../../../graphql';
import type { Cost } from './costs';
import { convertToDefaultCurrency } from '../../../state/zustand';
import { cargoAmountToSystemUnit } from '../../../constants/cargoUnits';
import type { ScenarioOrder } from '../../../lib/planningData';
import { DefaultMap } from '@seaber/common/lib/collections';

export function useVoyageExtraCosts(
  timeline: EventTimeline,
  focusedOrderIds: Set<number> | undefined,
  incomingVoyageChartersById: PnlData['incomingVoyageChartersById'],
  orderById: Map<number, ScenarioOrder> | null,
) {
  const voyageExtraCosts = useMemo(() => {
    const orders = timeline.voyageGroups.at(0)?.orders ?? [];
    const timelineVoyageCharterIds = new Set(
      compact(
        orders.map((o) => o.incomingVoyageCharterRelation?.voyageCharterId),
      ),
    );

    const loadedAmounts = getTimelineLoadedAmounts(timeline, focusedOrderIds);

    const extraCosts = getExtraCostsForLoadedAmount(
      loadedAmounts,
      timelineVoyageCharterIds,
      incomingVoyageChartersById,
      orderById,
    );

    return extraCosts;
  }, [timeline, focusedOrderIds, incomingVoyageChartersById, orderById]);

  return voyageExtraCosts;
}

export type GetExtraCostsForLoadedAmount = (
  loadedAmounts: LoadedAmount[],
  voyageCharterIds: Set<number>,
  incomingVoyageChartersById: Map<number, VoyageCosts_VoyageCharterFragment>,
) => Cost[];

export function getExtraCostsForLoadedAmount(
  loadedAmounts: LoadedAmount[],
  voyageCharterIds: Set<number>,
  incomingVoyageChartersById: Map<number, VoyageCosts_VoyageCharterFragment>,
  orderById: Map<number, ScenarioOrder> | null,
): Cost[] {
  const extraCosts: Cost[] = [];

  /* Extra costs */
  voyageCharterIds.forEach((voyageCharterId) => {
    const voyageCharter = incomingVoyageChartersById.get(voyageCharterId);
    if (!voyageCharter) return;

    const extraCostsOfVc = getVoyageCharterExtraCosts(
      voyageCharter,
      loadedAmounts,
      orderById,
    );

    extraCosts.push(...extraCostsOfVc);
  });

  return extraCosts;
}

const getOrderIdsByIncomingVoyageCharterId = memoize(
  (orderById: Map<number, ScenarioOrder> | null) => {
    const orderIdsByIncomingVoyageCharterId = new DefaultMap<
      number,
      Set<number>
    >(() => new Set());
    for (const o of orderById?.values() ?? []) {
      const incoming = o.incomingVoyageCharterRelation;
      const vcId = incoming?.voyageCharterId;
      if (!vcId) continue;
      orderIdsByIncomingVoyageCharterId.get(vcId).add(o.id);
    }
    return orderIdsByIncomingVoyageCharterId;
  },
);

function getVoyageCharterExtraCosts(
  voyageCharter: VoyageCosts_VoyageCharterFragment,
  loadedAmounts: LoadedAmount[],
  orderById: Map<number, ScenarioOrder> | null,
) {
  const costs: Cost[] = [];

  const orderIds = getOrderIdsByIncomingVoyageCharterId(orderById).get(
    voyageCharter.id,
  );

  const extraCosts = getVoyageCharterExtraCostData(voyageCharter);

  extraCosts.forEach((extraCost) => {
    switch (extraCost.type) {
      case VoyageCharterCostType.LUMPSUM: {
        const convertedAmount = extraCost.currency
          ? (convertToDefaultCurrency(extraCost.amount, extraCost.currency) ??
            extraCost.amount)
          : extraCost.amount;

        costs.push({
          type: 'EXTRA_COST',
          name: extraCost.name ?? `Extra cost ${extraCost.id}`,
          value: convertedAmount,
          locations: [],
          numShipments: 1,
          params: [
            {
              name: 'Extra cost type',
              value: 'Lump sum',
              source: 'SEABER',
            },
            {
              name: 'Amount',
              value: extraCost.amount,
              source: 'SEABER',
            },
            {
              name: 'Description',
              value: extraCost.description,
              source: 'SEABER',
            },
          ],
        });

        break;
      }
      case VoyageCharterCostType.RATE: {
        const total = loadedAmounts
          .filter((loaded) => orderIds.has(loaded.orderId) && loaded.cargoUnit)
          .reduce((sum, loaded) => {
            const loadedUnit = loaded.cargoUnit;
            return loadedUnit
              ? sum + cargoAmountToSystemUnit(loaded.cargoAmount, loadedUnit)
              : sum;
          }, 0);

        const amount = total * extraCost.amount;
        const convertedAmount = extraCost.currency
          ? (convertToDefaultCurrency(amount, extraCost.currency) ?? amount)
          : amount;

        costs.push({
          type: 'EXTRA_COST',
          name: extraCost.name ?? `Extra cost ${extraCost.id}`,
          value: convertedAmount,
          locations: [],
          numShipments: 1,
          params: [
            {
              name: 'Extra cost type',
              value: 'Rate',
              source: 'SEABER',
            },
            {
              name: 'Rate',
              value: extraCost.amount,
              source: 'SEABER',
            },
            {
              name: 'Amount',
              value: total,
              source: 'SEABER',
            },
            {
              name: 'Description',
              value: extraCost.description,
              source: 'SEABER',
            },
          ],
        });

        break;
      }
      default: {
        console.log('unknown extra cost type', extraCost);
        break;
      }
    }
  });

  return costs;
}

function getVoyageCharterExtraCostData(
  voyageCharter: VoyageCosts_VoyageCharterFragment,
) {
  return compact(
    voyageCharter.voyageCharterExtraCostsByVoyageCharterId.nodes,
  ).map(({ id, amount, name, type, description }) => ({
    amount: Number(amount),
    currency: voyageCharter.currency ?? null,
    description: description ?? null,
    id,
    name: name ?? null,
    type: type ?? null,
  }));
}
